import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";

const ElectronicSignature = ({ onClose, onConfirm }) => {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const [agreed, setAgreed] = useState(false);
  const [visible, setVisible] = useState(false);
  const [userName, setUserName] = useState("의뢰인");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [userData, setUserData] = useState(null);
  const [successFeePercentage, setSuccessFeePercentage] = useState(10);
  const [lawsuitAmount, setLawsuitAmount] = useState(5000);

  // 페이지 로드 시 모달이 하단에서 올라오도록 설정
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // 성공보수 퍼센티지와 소송 금액 가져오기
        const feePercentage = Cookies.get("lawsuit_success_fee_percentage");
        if (feePercentage) {
          setSuccessFeePercentage(parseInt(feePercentage, 10));
        }

        const amount = Cookies.get("lawsuit_amount");
        if (amount) {
          setLawsuitAmount(parseInt(amount, 10));
        }

        const token = Cookies.get(
          API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN,
        );

        if (!token) {
          throw new Error("인증 토큰이 없습니다. 다시 로그인해주세요.");
        }

        const response = await fetch(`${API_ENDPOINTS.BASE_URL}/clients/me`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error("사용자 정보를 가져오는데 실패했습니다.");
        }

        const data = await response.json();
        setUserData(data);
      } catch (err) {
        setError(err.message || "사용자 정보를 가져오는데 실패했습니다.");
      } finally {
        setVisible(true);
      }
    };

    fetchUserData();
  }, []);

  // 모달 닫기 처리
  const handleClose = () => {
    setVisible(false);
    setTimeout(() => {
      if (onClose) onClose();
    }, 300);
  };

  // 동의 처리
  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
  };

  const modalVariants = {
    hidden: {
      y: "100%",
      opacity: 1,
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 300,
      },
    },
    exit: {
      y: "100%",
      opacity: 1,
      transition: {
        duration: 0.3,
      },
    },
  };

  return (
    <ModalOverlay onClick={handleClose}>
      <AnimatePresence>
        {visible && (
          <ModalContainer
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>듀오 소송 위임계약서</ModalTitle>
              <CloseButton onClick={handleClose}>&times;</CloseButton>
            </ModalHeader>
            <ModalBody>
              <AgreementTitle>
                <strong>소송 위임계약서</strong>
              </AgreementTitle>

              <AgreementParties>
                <div>사 건 명 : 듀오 개인정보 유출사태 손해배상청구</div>
                <div>위임인(갑): {userData?.name || ""}</div>
                <div>수임인(을): 법무법인 더 에이치 황해</div>
              </AgreementParties>

              <SectionContainer>
                <Section>
                  <SectionTitle>제1조 (권한의 부여)</SectionTitle>
                  <SectionContent>
                    ① "갑"은 이 사건 소송대리사무를 처리하기 위하여 별첨
                    소송위임장에 기재된 권한(개인정보보호법령상 분쟁 조정절차의
                    대리를 포함한다)을 "을"에게 부여한다.
                    {"\n"}② 본 위임은 제1심뿐만 아니라 항소심·상고심을 포함한 본
                    사건의 모든 심급에 미치며, "갑"이 서면 또는 이에 준하는
                    명시적 방법으로 위임 종료 의사를 표시하지 아니하는 한 "을"은
                    각 심급의 절차를 계속하여 수행한다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제2조 (착수비용)</SectionTitle>
                  <SectionContent>
                    ① "갑"은 이 사건의 착수비용으로 금 10,000원(￦10,000)을
                    "을"에게 지급한다.
                    {"\n"}② "을"은 "갑"의 결제가 완료됨과 동시에 본
                    위임사무(사건 검토·자료 분석·당사자 확인 등 소송 준비행위를
                    포함하며, 이하 같다)에 즉시 착수한다.
                    {"\n"}③ 착수비용은 거래관행상 현저하게 염가이며, "을"이 결제
                    완료와 동시에 본 위임사무에 즉시 착수하는 점을 고려하여,
                    "갑"은 본 계약 체결 이후에는 "을"에게 착수비용의 반환을
                    청구할 수 없다. 다만, "을"의 귀책사유로 인하여 본 위임사무가
                    종료된 경우에는 그러하지 아니하다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제3조 (추가비용)</SectionTitle>
                  <SectionContent>
                    본 사건의 모든 심급의 진행에 따라 발생하는 인지대·송달료 등
                    일체의 실비는 "을"이 부담하며, "갑"은 제2조의 착수비용 외에
                    이 사건 위임사무를 처리하는 추가비용은 없는 것으로 한다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제4조 (성공보수금)</SectionTitle>
                  <SectionContent>
                    ① 이 사건에서 승소(일부 승소·조정금·합의금·보상금 지급을
                    포함한다)할 경우, "갑"이 승소로 얻은 경제적
                    이익가액(판결원리금·조정금·합의금·보상금을 포함하며, 이하
                    '판결금 등'이라 한다)을 기준으로 다음 각 호의 비율을
                    적용하여 산정한 성공보수금을 지급한다.
                    {"\n"}1. 제1심 판결로 종결된 경우 : 10%(부가가치세 별도,
                    이하 같음)
                    {"\n"}2. 항소심 판결로 종결된 경우 : 12%
                    {"\n"}3. 상고심 판결로 종결된 경우 : 15%
                    {"\n"}4. 합의·조정·화해 등 판결 외의 방법으로 종결된 경우 :
                    각 심급별 성공보수율에 따름
                    {"\n\n"}② 본 조에서 '보상금'이라 함은, 듀오측의 손해배상책임
                    인정 여부와 관계없이 소송절차의 내외를 불문하고 듀오측이
                    일방적으로 또는 합의에 의하여 정하여 "갑"에게 보상 차원에서
                    제공하는 일체의 경제적 이익을 말한다.
                    {"\n"}③ "을"이 "갑"을 대신하여 판결금 등을 수령하는 경우,
                    "을"은 수령일로부터 7일 이내에 성공보수금을 공제한 잔액을
                    "갑"이 지정한 계좌에 입금한다. "갑"이 직접 판결금 등을
                    수령하는 경우, "갑"은 수령일로부터 7일 이내에 성공보수금을
                    "을"이 지정하는 계좌에 입금하며, 그 지급이 지체되는 경우 연
                    12%의 지연손해금을 가산하여 지급한다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제5조 (승소로 보는 경우)</SectionTitle>
                  <SectionContent>
                    다음 각 호의 어느 하나에 해당하는 경우에는 승소한 것으로
                    보아 성공보수금을 지급한다.
                    {"\n"}1. "을"이 위임사무에 착수한 후 "갑"이 임의로 합의,
                    청구의 포기, 인낙, 화해, 신청 또는 소의 취하, 상소 취하 등
                    청구 포기의 효력을 발생하게 하거나 분쟁을 종결하는 행위를 한
                    때{"\n"}2. "을"이 위임사무에 착수한 후 "갑"이 임의로
                    위임계약을 해제·해지하거나, "갑"의 귀책사유로 인해 계약이
                    해지된 경우
                    {"\n"}3. "갑"이 본 계약상 의무를 이행하지 아니하는 등 "갑"의
                    책임 있는 사유로 인하여 "을"이 사건을 수행할 수 없어 사임한
                    경우
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제6조 (계약의 해제)</SectionTitle>
                  <SectionContent>
                    "갑"이 사건의 내용을 허위로 진술하거나 본 계약에서 정한
                    의무를 이행하지 않을 경우, "을"은 본 계약을 해지하고 소송
                    수행을 중단할 수 있다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제7조 (관할에 대한 합의)</SectionTitle>
                  <SectionContent>
                    본 계약과 관련한 분쟁은 인천지방법원 또는 "갑"의 주소지를
                    관할하는 법원 중 어느 한 곳을 합의관할 법원으로 한다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>
                    제8조 (전자적 서명 및 인장의 사용)
                  </SectionTitle>
                  <SectionContent>
                    ① "갑"은 본 계약 체결 및 소송 수행에 있어 전자적 방식의 자필
                    서명(태블릿·스마트폰 등 전자기기를 통하여 직접 입력한 서명을
                    포함한다)을 사용하는 것에 동의하며, 해당 전자적 자필 서명은
                    종이 서면에 한 자필 서명과 동일한 효력을 가진다.
                    {"\n"}② "갑"은 "을"이 본 계약에 따라 소송위임장, 소장 등
                    소송서류의 제출 및 판결금 등의 수령을 위하여 필요한 경우
                    "갑"의 전자적 인장(전자적 방식으로 조각·생성된 인장을
                    의미한다)을 조각·사용하는 것에 동의한다. "을"은 본 계약의
                    수행에 직접 필요한 범위에서만 이를 사용하며, 그 외의
                    목적으로는 사용하지 아니한다.
                  </SectionContent>
                </Section>

                <Section>
                  <SectionTitle>제9조 (개인정보 수집·이용 동의)</SectionTitle>
                  <SectionContent>
                    ① 일반 개인정보 수집 항목 : 성명, 생년월일, 주소, 연락처,
                    계좌 정보(은행명, 계좌번호, 예금주명), 전자서명, 듀오 서비스
                    가입 정보(가입 유형 — 무료회원/유료회원 구분, 가입비용 및
                    대략적인 가입일자), 피해 내용 및 관련 증빙자료
                    {"\n"}② 고유식별정보 수집 항목(「개인정보 보호법」 제24조에
                    따른 별도 동의 사항) : 주민등록번호(13자리), 신분증 사본 —
                    위 항목은 본인확인 및 소송 당사자 특정을 위하여
                    수집·이용하며, "갑"은 이에 별도로 동의한다.
                    {"\n"}③ 수집 목적 : 소송 수행 및 관련 법률 서비스 제공,
                    의뢰인과의 연락 및 소송 진행 상황 안내, 피해 사실 및 손해액
                    입증을 위한 자료 활용, 판결금 및 보상금 정산
                    {"\n"}④ 보유 기간 : 소송 종료 후 관련 법령에 따른 보존 기간
                    동안 보관(통상 소송 종료 후 5년)
                    {"\n"}⑤ "갑"은 위와 같은 개인정보 수집·이용에 동의하며,
                    "을"은 수집된 개인정보를 소송 목적 외에는 사용하지 아니한다.
                    다만, 법원, 듀오정보 주식회사(피고), 소송 수행을 위하여
                    필요한 감정인·전문가, 개인정보보호위원회 등 관련 행정기관에
                    제공하는 것은 소송 수행에 필요한 범위 내에서 허용된다.
                    {"\n"}⑥ 「개인정보 보호법」 제26조에 따른 개인정보 처리
                    위탁(수탁자, 위탁 업무의 내용 등) 및 그 밖에 개인정보 처리에
                    관한 상세 사항은 「개인정보 보호법」 제30조에 따라 "을"의
                    홈페이지에 게시된 개인정보처리방침에 따른다. "을"은 본 계약
                    체결에 앞서 "갑"이 동 처리방침의 내용을 확인할 수 있도록
                    안내한다.
                  </SectionContent>
                </Section>

                <SignatureDate>
                  {(() => {
                    const now = new Date();
                    return `${now.getFullYear()}년 ${
                      now.getMonth() + 1
                    }월 ${now.getDate()}일`;
                  })()}
                </SignatureDate>

                <SignatureSection>
                  <SignatureRow>
                    <SignatureLabel>"갑"</SignatureLabel>
                    <SignatureContent>
                      <SignatureLine>
                        성명 : {userData?.name || ""}
                      </SignatureLine>
                      <SignatureLine>
                        주소 : {userData?.address || ""},
                        {userData?.addressDetail || ""}
                      </SignatureLine>
                      <SignatureLine>
                        생년월일 :{" "}
                        {userData?.birthDate
                          ? (() => {
                              const bd = userData.birthDate
                                .toString()
                                .replace(/[^0-9]/g, "");
                              if (bd.length === 8) {
                                return `${bd.substring(0, 4)}년 ${bd.substring(
                                  4,
                                  6,
                                )}월 ${bd.substring(6, 8)}일`;
                              }
                              return bd;
                            })()
                          : ""}
                      </SignatureLine>
                      <SignatureLine>
                        연락처 : {userData?.phoneNumber || ""}
                      </SignatureLine>
                      <SignatureLine>(인)</SignatureLine>
                    </SignatureContent>
                  </SignatureRow>

                  <SignatureRow>
                    <SignatureLabel>"을"</SignatureLabel>
                    <SignatureContent>
                      <SignatureLine>법무법인 더 에이치 황해</SignatureLine>
                      <SignatureLine>대표변호사 천 상 현</SignatureLine>
                      <SignatureLine>
                        인천광역시 미추홀구 학익소로 60, 제1층 (학익동,
                        반도빌딩)
                      </SignatureLine>
                    </SignatureContent>
                  </SignatureRow>
                </SignatureSection>

                <CheckboxContainer onClick={() => setAgreed(!agreed)}>
                  <CheckboxImage
                    src={
                      agreed ? "/png/checked.png" : "/png/checkedinActive.png"
                    }
                    alt="체크박스"
                  />
                  <CheckboxLabel>
                    위 내용을 모두 확인하고 동의합니다
                  </CheckboxLabel>
                </CheckboxContainer>
              </SectionContainer>
            </ModalBody>
            <ModalFooter>
              <ConfirmButton onClick={handleConfirm} disabled={!agreed}>
                동의하고 계속하기
              </ConfirmButton>
            </ModalFooter>
          </ModalContainer>
        )}
      </AnimatePresence>
    </ModalOverlay>
  );
};

// 스타일 컴포넌트
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 999;
  touch-action: none;

  @media (min-width: 769px) {
    align-items: center;
  }
`;

const ModalContainer = styled(motion.div)`
  width: 100%;
  max-width: 500px;
  background-color: white;
  border-radius: 16px 16px 0 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 80vh;

  @media (min-width: 769px) {
    border-radius: 16px;
    max-width: 420px;
    max-height: 90vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  flex: 1;
  text-align: center;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  padding: 0;
  margin: 0;
  cursor: pointer;
`;

const ModalBody = styled.div`
  padding: 20px;
  overflow-y: auto;
  flex: 1;
  font-size: 14px;
  line-height: 1.6;
`;

const ModalFooter = styled.div`
  padding: 15px 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: center;
`;

const ConfirmButton = styled.button`
  display: flex;
  width: 100%;
  height: 56px;
  padding: 0 14px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: ${(props) => (props.disabled ? "#E5E5E5" : "#E31C20")};
  color: ${(props) => (props.disabled ? "#999" : "white")};
  font-size: 16px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  border: none;
  opacity: ${(props) => (props.disabled ? 0.8 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
`;

const AgreementTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
  text-align: center;
`;

const AgreementParties = styled.div`
  margin-bottom: 20px;
  line-height: 1.6;

  div {
    margin-bottom: 8px;
  }
`;

const SectionContainer = styled.div`
  margin-bottom: 20px;
`;

const Section = styled.div`
  margin-bottom: 15px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
`;

const SectionContent = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: #444;
  white-space: pre-line;
  margin-bottom: 15px;
`;

const SignatureDate = styled.div`
  margin: 20px 0;
  text-align: center;
  font-weight: 500;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 15px;
  cursor: pointer;
`;

const CheckboxImage = styled.img`
  width: 24px;
  height: 24px;
  margin-right: 10px;
`;

const CheckboxLabel = styled.div`
  font-size: 15px;
  font-weight: 500;
`;

const SignatureSection = styled.div`
  margin-top: 30px;
  margin-bottom: 20px;
`;

const SignatureRow = styled.div`
  display: flex;
  margin-bottom: 20px;
  align-items: flex-start;
`;

const SignatureLabel = styled.div`
  font-weight: 600;
  margin-right: 20px;
  min-width: 40px;
`;

const SignatureContent = styled.div`
  flex: 1;
  line-height: 1.8;
`;

const SignatureLine = styled.div`
  margin-bottom: 4px;
  font-size: 14px;
`;

export default ElectronicSignature;
